import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaExternalLinkAlt, FaShoppingCart, FaYoutube } from 'react-icons/fa';
import './UpcomingReleases.css';
import YoinImage00 from '../assets/print_yoin_000.webp';
import YoinImage01 from '../assets/print_yoin_001.webp';
import YoinImage02 from '../assets/print_yoin_002.webp';
import YoinImage03 from '../assets/print_yoin_003.webp';
import YoinImage04 from '../assets/print_yoin_004.webp';
import { getSendyListIds } from '../services/configService';
import { API_URL } from '../config';

function UpcomingReleases() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [upcomingListId, setUpcomingListId] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  let touchStartX = 0;
  let touchStartY = 0;

  // Cargar la configuración al iniciar
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { upcomingListId } = await getSendyListIds();
        setUpcomingListId(upcomingListId);
      } catch (error) {
        console.error('Error al cargar configuración:', error);
      }
    };
    
    loadConfig();
  }, []);

  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      
      const deltaX = touchStartX - touchEndX;
      const deltaY = touchStartY - touchEndY;

      // Solo detectar movimiento horizontal hacia la derecha
      if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX > 50) {
        window.location.href = '/';
      }
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const upcomingProduct = {
    name: "YOIN: The shadow of dawn",
    releaseDate: "Mark your calendars... October is the month.",
    description: "A spirit awakens in a dreamscape of feudal Japan, determined to reclaim what is his and free his home from darkness. With his memory fragmented but his will unbreakable, he faces unseen threats as he battles time and invaders.",
    isInternal: false,
    gifUrl: YoinImage00,
    images: [
      YoinImage01,
      YoinImage02,
      YoinImage03,
      YoinImage04
    ]
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Función para enmascarar email
  const maskEmail = (email) => {
    if (!email) return '[NO EMAIL]';
    const [name, domain] = email.split('@');
    if (!domain) return '[INVALID EMAIL]';
    return `${name.charAt(0)}***@${domain}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setSubmitStatus('');
    setIsLoading(true);

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    if (!upcomingListId) {
      setEmailError('System error: Could not determine list ID');
      setIsLoading(false);
      return;
    }

    try {
      console.log('Iniciando proceso de suscripción a lista de lanzamientos');
      console.log('Email enmascarado:', maskEmail(email));
      
      const response = await fetch(`${API_URL}/newsletter/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          email: email,
          name: null,
          lists: ['upcoming']
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('Respuesta recibida - Status:', response.status);
      const data = await response.json();

      if (data.success) {
        console.log('Suscripción exitosa para email enmascarado:', maskEmail(email));
        setSubmitStatus('Thank you for subscribing!');
        setEmail('');
      } else {
        console.error('Error en suscripción:', data.message);
        setEmailError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error de conexión:', error.message);
      setEmailError('Connection error. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Funciones para el modal de imágenes
  const openImageModal = (image) => {
    setSelectedImage(image);
    setModalOpen(true);
    document.body.style.overflow = 'hidden'; // Evitar scroll cuando el modal está abierto
  };

  const closeImageModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
    document.body.style.overflow = 'auto'; // Restaurar scroll
  };

  // Cerrar modal al hacer clic fuera de la imagen
  const handleModalClick = (e) => {
    if (e.target.className === 'image-modal') {
      closeImageModal();
    }
  };

  return (
    <div className="upcoming-container">
      <motion.div 
        className="upcoming-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="media-section">
          <div className="main-media">
            <img 
              src={upcomingProduct.gifUrl} 
              alt={`${upcomingProduct.name} preview`}
              className="product-gif"
              onClick={() => openImageModal(upcomingProduct.gifUrl)}
            />
          </div>

          <div className="product-images">
            {upcomingProduct.images.map((image, index) => (
              <motion.img 
                key={index}
                src={image}
                alt={`${upcomingProduct.name} screenshot ${index + 1}`}
                whileHover={{ scale: 1.05 }}
                onClick={() => openImageModal(image)}
              />
            ))}
          </div>
        </div>

        <div className="product-details">
          <div className="details-header">
            <h2>{upcomingProduct.name}</h2>
            <div className="release-info">
              <span className="release-date">
                {upcomingProduct.releaseDate}
              </span>
              <span className="release-type">
                {upcomingProduct.isInternal ? (
                  <><FaShoppingCart /> Internal Release</>
                ) : (
                  <a 
                    href="https://store.steampowered.com/app/3750350/YOIN_The_shadow_of_dawn/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="steam-link"
                  >
                    <FaExternalLinkAlt /> Wishlist on Steam
                  </a>
                )}
              </span>
              <a 
                href="https://www.youtube.com/watch?v=fzf-mzKwB4k" 
                target="_blank" 
                rel="noopener noreferrer"
                className="youtube-link"
              >
                <FaYoutube /> Watch Trailer
              </a>
            </div>
          </div>
          
          <p className="product-description">{upcomingProduct.description}</p>

          <div className="subscription-section">
            <form onSubmit={handleSubmit} className="subscription-form">
              <div className="input-group">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className={emailError ? 'error' : ''}
                />
                <button type="submit" disabled={isLoading}>
                  {isLoading ? 'Subscribing...' : 'Stay Updated!'}
                </button>
              </div>
              {emailError && <p className="error-message">{emailError}</p>}
              {submitStatus && <p className="success-message">{submitStatus}</p>}
            </form>
          </div>
        </div>
      </motion.div>

      {/* Modal para mostrar imágenes en tamaño completo */}
      {modalOpen && (
        <div className="image-modal" onClick={handleModalClick}>
          <div className="modal-content">
            <img src={selectedImage} alt="Imagen ampliada" />
            <button className="close-modal" onClick={closeImageModal}>×</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpcomingReleases;